import React, {useEffect, useLayoutEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {useLocation} from 'react-router-dom'
import './app-tooltips.css'

// Explicit data-tooltip attributes keep native browser titles out of the UI.
// A portal prevents table scrolling containers from clipping the tooltip.
export default function AppTooltips() {
  const [active,setActive]=useState(null),[position,setPosition]=useState(null)
  const current=useRef(null),bubble=useRef(null),timer=useRef(null)
  const location=useLocation()
  function close(){clearTimeout(timer.current);current.current=null;setActive(null);setPosition(null)}
  useEffect(()=>{close()},[location.key])
  useEffect(()=>{
    const find=node=>node instanceof Element?node.closest('[data-tooltip]'):null
    const show=(anchor,described=anchor)=>{
      const text=anchor?.getAttribute('data-tooltip')?.trim()
      if(!text)return
      clearTimeout(timer.current)
      if(current.current?.anchor===anchor)return
      const next={anchor,described,text};current.current=next;setPosition(null);setActive(next)
    }
    const leave=()=>{clearTimeout(timer.current);timer.current=setTimeout(close,120)}
    const over=e=>{
      if(e.pointerType==='touch')return
      if(bubble.current?.contains(e.target)){clearTimeout(timer.current);return}
      const anchor=find(e.target);if(anchor)show(anchor)
    }
    const out=e=>{
      const anchor=current.current?.anchor
      if(anchor?.contains(e.relatedTarget)||bubble.current?.contains(e.relatedTarget))return
      if(anchor?.contains(e.target)||bubble.current?.contains(e.target))leave()
    }
    const focus=e=>{const anchor=find(e.target);if(anchor)show(anchor,e.target);else close()}
    const blur=e=>{if(current.current?.anchor?.contains(e.target))leave()}
    const escape=e=>{if(e.key==='Escape')close()}
    const down=e=>{if(!bubble.current?.contains(e.target))close()}
    const scroll=e=>{if(!bubble.current?.contains(e.target))close()}
    document.addEventListener('pointerover',over,true);document.addEventListener('pointerout',out,true)
    document.addEventListener('focusin',focus,true);document.addEventListener('focusout',blur,true)
    document.addEventListener('keydown',escape,true);document.addEventListener('pointerdown',down,true)
    document.addEventListener('scroll',scroll,true);window.addEventListener('resize',close)
    window.visualViewport?.addEventListener('resize',close)
    return ()=>{
      clearTimeout(timer.current)
      document.removeEventListener('pointerover',over,true);document.removeEventListener('pointerout',out,true)
      document.removeEventListener('focusin',focus,true);document.removeEventListener('focusout',blur,true)
      document.removeEventListener('keydown',escape,true);document.removeEventListener('pointerdown',down,true)
      document.removeEventListener('scroll',scroll,true);window.removeEventListener('resize',close)
      window.visualViewport?.removeEventListener('resize',close)
    }
  },[])
  useLayoutEffect(()=>{
    if(!active||!bubble.current)return
    if(!active.anchor.isConnected){close();return}
    const r=active.anchor.getBoundingClientRect(),b=bubble.current.getBoundingClientRect()
    const viewport=window.visualViewport,left=viewport?.offsetLeft||0,top=viewport?.offsetTop||0
    const width=viewport?.width||innerWidth,height=viewport?.height||innerHeight
    setPosition({left:Math.max(left+8,Math.min(r.left+(r.width-b.width)/2,left+width-b.width-8)),top:Math.max(top+8,Math.min(r.top-b.height-8>=top+8?r.top-b.height-8:r.bottom+8,top+height-b.height-8))})
    const element=active.described,id='maintainiq-action-tooltip'
    const ids=new Set((element.getAttribute('aria-describedby')||'').split(/\s+/).filter(Boolean));ids.add(id)
    element.setAttribute('aria-describedby',[...ids].join(' '))
    return ()=>{
      const remaining=(element.getAttribute('aria-describedby')||'').split(/\s+/).filter(v=>v&&v!==id)
      if(remaining.length)element.setAttribute('aria-describedby',remaining.join(' '));else element.removeAttribute('aria-describedby')
    }
  },[active])
  if(!active)return null
  return createPortal(<div ref={bubble} id="maintainiq-action-tooltip" role="tooltip" className="miq-action-tooltip" style={{...position,visibility:position?'visible':'hidden'}}>{active.text}</div>,active.anchor.closest('dialog')||document.body)
}
